import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Paper, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  Card, 
  CardContent, 
  CardActions
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import OnboardingModal from '../components/OnboardingModal';

const VerifyPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);
  const [tokenFound, setTokenFound] = useState(false);
  const { 
    verifyToken, 
    isAuthenticated, 
    multipleAccounts, 
    selectAccount, 
    inactiveUser, 
    user,
    createNewOrg,
    showOnboarding,
    setShowOnboarding,
    landingFormData
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasVerifiedRef = useRef(false);
  const landingFormDataRef = useRef(landingFormData);
  const hasQueuedExpressNavigationRef = useRef(false);

  useEffect(() => {
    if (landingFormData) {
      landingFormDataRef.current = landingFormData;
    }
  }, [landingFormData]);
  
  useEffect(() => {
    const verifyMagicLink = async () => {
      // Skip verification if we've already tried
      if (hasVerifiedRef.current) {
        return;
      }
      
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');
      const isOAuth = searchParams.get('oauth') === 'true';
      const oauthError = searchParams.get('error');
      
      // Handle OAuth errors
      if (oauthError) {
        let errorMessage = 'Authentication failed. Please try again.';
        if (oauthError === 'oauth_failed') {
          errorMessage = 'Google sign-in failed. Please try again.';
        } else if (oauthError === 'oauth_error') {
          errorMessage = 'An error occurred during sign-in. Please try again.';
        }
        setError(errorMessage);
        setVerifying(false);
        return;
      }
      
      if (!token) {
        setError('Invalid link. No verification token found.');
        setVerifying(false);
        return;
      }
      
      setTokenFound(true);
      // Mark that we've attempted verification to prevent duplicate attempts
      hasVerifiedRef.current = true;
      
      try {
        // Pass OAuth flag to the verifyToken function in AuthContext
        // The AuthContext will handle calling the correct backend endpoint
        await verifyToken(token, isOAuth);
        // Redirect happens in context, except for inactiveUser
      } catch (err) {
        const errorMessage = isOAuth 
          ? 'Failed to complete Google sign-in. Please try again.' 
          : 'Failed to verify your magic link. Please try logging in again.';
        setError(errorMessage);
        logger.error('Verification error:', err);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyMagicLink();
  }, [location.search, verifyToken]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      verifying ||
      multipleAccounts ||
      inactiveUser ||
      showOnboarding
    ) {
      return;
    }

    const latestLandingData = landingFormDataRef.current;

    if (latestLandingData && !hasQueuedExpressNavigationRef.current) {
      const navigationData = {
        fromLanding: true,
        mode: latestLandingData.mode,
        formData: latestLandingData.data
      };

      try {
        sessionStorage.setItem('landing_form_navigation', JSON.stringify(navigationData));
      } catch (error) {
        logger.warn('Failed to persist landing navigation data during verify redirect', error);
      }

      const targetPath = latestLandingData.mode === 'simulation' 
        ? '/simulations/create'
        : '/ideas/create';

      logger.info('VerifyPage triggering express navigation fallback:', {
        targetPath,
        hasFormData: !!navigationData.formData
      });

      hasQueuedExpressNavigationRef.current = true;
      navigate(targetPath, { replace: true, state: navigationData });
      return;
    }

    if (!latestLandingData) {
      navigate('/dashboard', { replace: true });
    }
  }, [
    isAuthenticated,
    verifying,
    multipleAccounts,
    inactiveUser,
    showOnboarding,
    navigate
  ]);
  
  const handleSelectAccount = async (userId: string) => {
    try {
      await selectAccount(userId);
    } catch (error) {
      logger.error('Error selecting account:', error);
      setError('Failed to select account. Please try again.');
    }
  };
  
  // If still verifying, show loading
  if (verifying) {
    return (
      <>
        {showOnboarding && (
          <OnboardingModal 
            isOpen={showOnboarding} 
            onClose={() => setShowOnboarding(false)}
            expressMode={!!landingFormData}
            landingFormData={landingFormData}
          />
        )}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            mt: 8 
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Verifying your identity...
          </Typography>
        </Box>
      </>
    );
  }
  
  // If verification failed
  if (error) {
    return (
      <>
        {showOnboarding && (
          <OnboardingModal 
            isOpen={showOnboarding} 
            onClose={() => setShowOnboarding(false)}
            expressMode={!!landingFormData}
            landingFormData={landingFormData}
          />
        )}
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          
          <Button 
            component={RouterLink} 
            to="/login" 
            variant="contained" 
            fullWidth
          >
            Back to Login
          </Button>
        </Paper>
      </>
    );
  }
  
  // If authenticated and not showing multiple accounts or inactive user, redirect to dashboard
  if (isAuthenticated && !multipleAccounts && !inactiveUser && !verifying) {
    return (
      <>
        {showOnboarding && (
          <OnboardingModal 
            isOpen={showOnboarding} 
            onClose={() => setShowOnboarding(false)}
            expressMode={!!landingFormData}
            landingFormData={landingFormData}
          />
        )}
        {!showOnboarding && (
          <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
            <CircularProgress size={30} sx={{ mb: 2 }} />
            <Typography>
              Finalizing your workspace setup...
            </Typography>
          </Paper>
        )}
      </>
    );
  }
  
  // Show selection UI for multiple accounts
  if (multipleAccounts && multipleAccounts.length > 0 && !verifying) {
    return (
      <>
        {showOnboarding && (
          <OnboardingModal 
            isOpen={showOnboarding} 
            onClose={() => setShowOnboarding(false)}
            expressMode={!!landingFormData}
            landingFormData={landingFormData}
          />
        )}
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Select Your Account
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            We found multiple accounts associated with your email address. Please select which account you want to access:
          </Typography>
          
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {multipleAccounts.map((account, index) => (
              <React.Fragment key={account.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  alignItems="flex-start"
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => handleSelectAccount(account.id)}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body1">
                      {account.organizationName || 'No Organization'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {account.firstName && account.lastName 
                        ? `${account.firstName} ${account.lastName}` 
                        : account.email}
                      {` — ${account.role}`}
                    </Typography>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
          
          <Divider sx={{ my: 3 }} />
          
          <Button
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            onClick={createNewOrg}
          >
            Create a New Organization
          </Button>
        </Paper>
      </>
    );
  }
  
  // Render inactive user view - distinguish between deactivated and new users
  if (inactiveUser && user && !verifying) {
    // Case 1: New user needing onboarding (active but no organization)
    if (user.isActive && !user.organizationId) {
      return (
        <>
          {showOnboarding && (
            <OnboardingModal
              isOpen={showOnboarding}
              onClose={() => setShowOnboarding(false)}
              expressMode={!!landingFormData}
              landingFormData={landingFormData}
            />
          )}
          {!showOnboarding && (
            <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
              <CircularProgress size={30} sx={{ mb: 2 }} />
              <Typography>
                Setting up your workspace...
              </Typography>
            </Paper>
          )}
        </>
      );
    }

    // Case 2: Deactivated user (manually deactivated by admin)
    const organizationName = user.organizationName || 'your organization';

    return (
      <>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Your account has been deactivated
          </Alert>

          <Typography variant="h5" component="h1" gutterBottom>
            Account Inactive
          </Typography>

          <Typography variant="body1" paragraph>
            Your account associated with <strong>{organizationName}</strong> has been deactivated by an administrator.
          </Typography>

          <Typography variant="body1" paragraph>
            You can create a new organization to continue using the platform.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={createNewOrg}
            sx={{ mt: 2 }}
          >
            Create New Organization
          </Button>
        </Paper>
      </>
    );
  }
  
  // Default view when token was found but none of the above conditions were met
  if (tokenFound && !verifying) {
    return (
      <>
        {showOnboarding && (
          <OnboardingModal 
            isOpen={showOnboarding} 
            onClose={() => setShowOnboarding(false)}
            expressMode={!!landingFormData}
            landingFormData={landingFormData}
          />
        )}
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
          <CircularProgress size={30} sx={{ mb: 2 }} />
          <Typography>
            Completing authentication...
          </Typography>
        </Paper>
      </>
    );
  }
  
  return (
    <>
      {/* Show OnboardingModal when needed */}
      {showOnboarding && (
        <OnboardingModal 
          isOpen={showOnboarding} 
          onClose={() => setShowOnboarding(false)} 
        />
      )}
      
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Verification is still pending.
        </Alert>
        
        <Button 
          component={RouterLink} 
          to="/login" 
          variant="contained" 
          fullWidth
        >
          Back to Login
        </Button>
      </Paper>
    </>
  );
};

export default VerifyPage; 
